import { MessageContextObject } from "@/__generated__/graphql";
import { IObjectWithTypename } from "@/components/@types";
import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import { Markdown } from "@/components/molecules/Markdown";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { Objects } from "@/objects";

import type { StreamContext } from "@/components/organisms/ToolCallCard/ToolCallCard.tools";
import {
  TypedDataTable,
  generateMarkdownTable,
} from "@/components/organisms/ToolCallCard/ToolCallCardRichText";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

function PresentedMessageContext({
  context,
  autofocus = false,
}: {
  context: MessageContextObject;
  autofocus?: boolean;
}) {
  const nodeId = context.node?.id;
  const identifier = nodeId || "";
  const typename = nodeId
    ? (context.node as { __typename?: string }).__typename ||
      atob(identifier).split(":")[0]
    : "";

  const { data, loading } = useBackendQuery(
    Queries.NODE_QUERY_WITH_PENDING_CHANGE,
    {
      variables: nodeId ? { id: nodeId } : undefined,
      skip: !nodeId,
    },
  );

  const { store } = useNodeSetup({
    mode: "get",
    node: data?.node as IObjectWithTypename,
    typename,
  });

  if (!nodeId) return null;

  if (loading) {
    return (
      <div className="p-2 w-full">
        <Skeleton variant="rectangular" width="100%" height={100} />
      </div>
    );
  }

  if (!data?.node) return null;

  return (
    <div
      key={context.node.id}
      className="w-full [border-width:1px] [border-style:solid] border-border rounded-md overflow-hidden mt-2 mb-2"
    >
      <NodeLayout
        autofocus={autofocus}
        store={store}
        isLoading={loading}
        size="sm"
      />
    </div>
  );
}

export function MessageContext({
  context,
  isAgent = true,
  autofocus = false,
}: {
  context: MessageContextObject;
  isAgent?: boolean;
  autofocus?: boolean;
}) {
  const nodeId = context.node?.id;
  const identifier = nodeId || "";
  const typename = nodeId
    ? (context.node as { __typename?: string }).__typename ||
      atob(identifier).split(":")[0]
    : "";
  const objectType = typename ? Objects[typename] : undefined;
  const optimisticName = (context.node as { name?: string })?.name;

  const { data, loading } = useBackendQuery(
    Queries.NODE_QUERY_WITH_PENDING_CHANGE,
    {
      variables: nodeId ? { id: nodeId } : undefined,
      skip: !nodeId,
    },
  );

  const fetchedName = data?.node ? objectType?.getTitle(data?.node) : null;
  const name = fetchedName || optimisticName || objectType?.humanName;

  if (!nodeId) return null;

  if (context.isPresented && isAgent) {
    return <PresentedMessageContext context={context} autofocus={autofocus} />;
  }

  if (context.isPresented) {
    return (
      <span className="inline align-middle">
        <FieldReference
          value={{
            __typename: typename,
            name,
            id: identifier,
            identifier,
          }}
          valueOptions={{ style: "related", isNullable: false }}
          isMultiline={false}
        />
      </span>
    );
  }

  return (
    <span
      key={context.node.id}
      className="inline w-fit flex-row transform-[translateY(-2px)] max-h-5 items-center justify-center"
    >
      <FieldReference
        value={{
          __typename: typename,
          name,
          id: identifier,
          identifier,
        }}
        valueOptions={{ style: "text", isNullable: false }}
        style={{ paddingLeft: 0, paddingRight: 0 }}
        isMultiline={false}
      />
    </span>
  );
}

function StreamingNodeDisplay({
  nodeId,
  typename,
  autofocus = false,
}: {
  nodeId: string;
  typename: string;
  autofocus?: boolean;
}) {
  const { data, loading } = useBackendQuery(
    Queries.NODE_QUERY_WITH_PENDING_CHANGE,
    {
      variables: nodeId ? { id: nodeId } : undefined,
      skip: !nodeId,
    },
  );

  const { store } = useNodeSetup({
    mode: "get",
    node: data?.node as IObjectWithTypename,
    typename,
  });

  if (loading) {
    return (
      <div className="p-2 w-full">
        <Skeleton variant="rectangular" width="100%" height={100} />
      </div>
    );
  }

  if (!data?.node) return null;

  return (
    <div className="w-full [border-width:1px] [border-style:solid] border-border rounded-md overflow-hidden mt-2 mb-2">
      <NodeLayout
        autofocus={autofocus}
        store={store}
        isLoading={loading}
        size="sm"
      />
    </div>
  );
}

export function StreamContextsDisplay({
  contexts,
  autofocus = false,
}: {
  contexts: StreamContext[];
  autofocus?: boolean;
}) {
  const groupedByType: Record<string, StreamContext[]> = {};
  for (const ctx of contexts) {
    const typename = ctx.node.__typename || "Unknown";
    if (!groupedByType[typename]) groupedByType[typename] = [];
    groupedByType[typename].push(ctx);
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {Object.entries(groupedByType).map(([typename, ctxs]) => {
        const nodes = ctxs.map((c) => c.node);
        const displayTypename = typename.replace("Object", "");

        if (nodes.length > 1) {
          const hasObjectDef = Objects[typename as keyof typeof Objects];
          if (hasObjectDef) {
            return (
              <div key={typename} className="w-full overflow-auto">
                <Typography
                  level="body-xs"
                  className="text-muted-foreground/80 mb-1"
                >
                  {nodes.length} {displayTypename}(s) found
                </Typography>
                <TypedDataTable
                  items={nodes as Record<string, unknown>[]}
                  typename={typename}
                />
              </div>
            );
          }

          const markdownTable = generateMarkdownTable(
            nodes as Record<string, unknown>[],
          );
          return (
            <div key={typename} className="w-full min-w-0">
              <Typography
                level="body-xs"
                className="text-muted-foreground/80 mb-1"
              >
                {nodes.length} {displayTypename}(s) found
              </Typography>
              <Markdown resize={0}>{markdownTable}</Markdown>
            </div>
          );
        }

        const node = nodes[0];
        return (
          <StreamingNodeDisplay
            key={typename + node.id}
            nodeId={node.id}
            typename={typename}
            autofocus={autofocus}
          />
        );
      })}
    </div>
  );
}
